import axios from "axios";
import createEnvironment from "../globel";
import { getLocalStorage, saveLocalStorage } from "../utils/localStorage";

import { resetSessionTimer } from "../utils/sessionManager";
import { refreshToken } from "./authService";

const ENV_VAR = createEnvironment();
const BASE_URL = ENV_VAR.BASE_URL;

const axiosInstance = axios.create({
    baseURL: BASE_URL,
    timeout: 120000,
});

let isRefreshing = false;
let failedQueue = []; // Will store failed requests in order

const processQueue = (error, token = null) => {
    failedQueue.forEach(promise => {
        if (error) {
            promise.reject(error);
        } else {
            promise.resolve(token);
        }
    });
    failedQueue = [];
};

// 🔹 Request Interceptor: attach token
axiosInstance.interceptors.request.use(
    async (config) => {
        const token = getLocalStorage("access_token");
        if (token) {
            config.headers["Authorization"] = `Bearer ${token}`;
        }
        return config;
    },
    (error) => Promise.reject(error)
);

// 🔹 Response Interceptor
axiosInstance.interceptors.response.use(
    (response) => {
        // In case backend embeds 401 in body with 200
        const originalRequest = response.config;
        if (
            (response?.data?.statusCode === 401 || response?.data?.status === 401) &&
            !originalRequest._retry &&
            !originalRequest.url?.includes("/auth/login/v2/refresh")
        ) {
            return handle401(originalRequest);
        }
        return response;
    },
    async (error) => {
        const originalRequest = error?.config || {};

        if (!error?.response) {
            return Promise.reject(error);
        }
        if (error.response.status !== 401) {
            return Promise.reject(error);
        }
        if (originalRequest._retry || originalRequest.url?.includes("/auth/login/v2/refresh")) {
            return Promise.reject(error);
        }

        return handle401(originalRequest);
    }
);

async function handle401(originalRequest) {
    if (isRefreshing) {
        // Put request in queue to retry later
        return new Promise((resolve, reject) => {
            failedQueue.push({ resolve, reject });
        }).then((token) => {
            originalRequest.headers["Authorization"] = `Bearer ${token}`;
            originalRequest._retry = true;
            return axiosInstance(originalRequest);
        });
    }
    // First request to trigger refresh
    originalRequest._retry = true;
    isRefreshing = true;
    try {
        const newToken = await refreshToken();
        // Store new token
        saveLocalStorage("access_token", newToken);
        // Resolve all queued requests
        processQueue(null, newToken);
        // Retry the original request now
        originalRequest.headers["Authorization"] = `Bearer ${newToken}`;
        return axiosInstance(originalRequest);
    } catch (err) {
        processQueue(err, null);
        return Promise.reject(err);
    } finally {
        isRefreshing = false;
    }
}


const getUrlForTemplates = (type) => {
    console.log(type);
    switch (type) {
        case "SMS":
            return "tdf/api/dltTemplate";
            break;
        case "IVR":
            return "tdf/api/ivrTemplate";
            break;
        case "EMAIL":
            return "tdf/api/email";
            break;
        default: return ""
            break;
    }
}

export const PATHS = {
    LOGIN_NEW: BASE_URL + "/auth/login/crm/token",
    LOGIN: BASE_URL + "/auth/login/crm",
    GET_CURRENT_USER_DETAIL: BASE_URL + "/auth/lms/lmsUserDetails",

    ADMIN_USER_LISTING: BASE_URL + "/auth/lms/listUser",
    ADMIN_USER_DETAIL: (id) => BASE_URL + "/auth/lms/getUser/" + id,
    ADMIN_ADD_USER: BASE_URL + "/auth/lms/addUser",
    ADMIN_EDIT_USER: (id) => BASE_URL + "/auth/lms/editUser/" + id,


    DOWNLOAD_LOAN_CSV_SAMPLE: (id) => BASE_URL + "/lms/app/v1/loan/" + id + "/statement",

    UPLOAD_EXCEL_FILE: (type) => BASE_URL + `/app/tdf/processExcel${type}`,
    UPLOAD_EXCEL_FILE_NEW: (type) => BASE_URL + `/app/tdf/processExcel/${type === "Loan" ? "loan" : "card"}`,

    // process admin
    PROCESS_ADMIN_LISTING: (type) => BASE_URL + `/app/tdf/v2/${type}ExcelMetaDataList`,
    PROCESS_ADMIN_LISTING_NEW: (type) => BASE_URL + `/app/tdf/${type}/${type}MetaDataList`,
    TEAM_LEAD_META_DATA_LISTING_NEW: (type) => BASE_URL + `/app/tdf/${type}/${type}TeamLeadMetaDataList`,

    PROCESS_ADMIN_RECORD_LISTING_WITH_FILE_ID: (type) => BASE_URL + `/app/tdf/v2/get${type}DataSummary`,
    PROCESS_ADMIN_RECORD_LISTING_WITH_FILE_ID_NEW: (type) => BASE_URL + `/app/tdf/${type === "Loan" ? "loan" : "card"}/${type === "Loan" ? "loan" : "card"}SummaryList`,
    CRAD_PAYMENT_META_DATA_LIST_WITH_FILE_ID: (type) => BASE_URL + `/app/tdf/${type === "Loan" ? "loan" : "card"}/${type === "Loan" ? "loan" : "card"}PaymentMetaDataList`,

    // file id details /app/tdf/loanExcelMetaData/24fa1a7393d247ec833e822f0ee22e52
    FILE_ID_DETAILS: (type, id) => BASE_URL + `/app/tdf/${type}ExcelMetaData/${id}`,
    // get all record listing 
    EXCEL_ALL_RECORD_LISTING: (type) => BASE_URL + `/app/tdf/${type}DataSummaryListing`,
    EXCEL_ALL_RECORD_LISTING_NEW: (type) => BASE_URL + `/app/tdf/${type === "loan" ? "loan" : "card"}/${type === "loan" ? "loan" : "card"}SummaryList`,
    //lms role listing
    CRM_ROLE_LISTING: BASE_URL + "/auth/lms/roleListing",
    CRM_ROLE_REPORTING_TO_LISTING: BASE_URL + "/auth/lms/reportingToListing",
    CRM_ROLE_TEAM_LEAD_LISTING: BASE_URL + "/auth/lms/teamLeadListing",
    CRM_ROLE_ASSIGN_TEAM_LEAD_LISTING: BASE_URL + "/auth/lms/allocationListing",

    CRM_ROLE_ASSIGN_TO_TEAM_LEADS: (type) => BASE_URL + `/app/tdf/${type}TeamLeadAllocation`,
    CRM_ROLE_ASSIGN_TO_TEAM_LEADS_NEW: (type) => BASE_URL + `/app/tdf/allocation/${type}TeamLeadAllocation`,

    CRM_ROLE_ASSIGN_TO_TELE_CALLERS_NEW: (type) => BASE_URL + `/app/tdf/allocation/${type}TeleCallerAllocation`,

    CRM_ROLE_RESET_TO_TEAM_LEADS: (type, id) => BASE_URL + `/app/tdf/reset${type === "loan" ? "Loan" : "Card"}TeamLeadAllocation/${id}`,
    CRM_ROLE_RESET_TO_TEAM_LEADS_NEW: (type, id) => BASE_URL + `/app/tdf/allocation/reset${type === "loan" ? "Loan" : "Card"}TeamLeadAllocation/${id}`,

    CRM_ROLE_RESET_TO_TELE_CALLER_BY_TEAM_LEADS_NEW: (type, id) => BASE_URL + `/app/tdf/allocation/reset${type === "loan" ? "Loan" : "Card"}TeleCallerAllocation/${id}`,

    CRM_ROLE_ASSIGN_TO_TELE_CALLER_LISTING: (type) => BASE_URL + `/app/tdf/listingFor${type}TeamLead`,
    CRM_ROLE_ASSIGN_TO_TELE_CALLERS: (type) => BASE_URL + `/app/tdf/${type}TeleCallerAllocation`,
    CRM_ROLE_RESET_TO_TELE_CALLERS: (type) => BASE_URL + `/app/tdf/reset${type === "loan" ? "Loan" : "Card"}TeleCallerAllocation`,

    // telecaller 
    TELE_CALLER_RECORD_LISTING: (type) => BASE_URL + `/app/tdf/listingFor${type === "loan" ? "Loan" : "Card"}TeleCaller`,
    TELE_CALLER_RECORD_LISTING_NEW: (type) => BASE_URL + `/app/tdf/${type === "loan" ? "loan" : "card"}/${type === "loan" ? "loan" : "card"}SummaryList`,
    TELE_CALLER_RECORD_DETAIL: (type, id) => BASE_URL + `/app/tdf/v2/${type}DataSummaryDetail/${id}`,
    TELE_CALLER_RECORD_DETAIL_NEW: (type, id) => BASE_URL + `/app/tdf/${type}/${type}Summary/${id}`,

    // disposition
    TELE_CALLER_SAVE_DISPOSITION: BASE_URL + `/app/disposition/saveDisposition`,
    TELE_CALLER_GET_DISPOSITION_LIST: (id) => BASE_URL + `/app/disposition/getDisposition/${id}`,

    // other record
    TELE_CALLER_OTHER_RECORD: (id) => BASE_URL + `/app/tdf/crm/getOtherRecords/${id}`,


    // tdf website users
    ADMIN_TDF_APP_USERS_LIST: BASE_URL + "/tdf/api/v2/profileListing",
    ADMIN_TDF_APP_STAGE_COUNT: BASE_URL + "/tdf/api/totalActivityStatusLogging",
    ADMIN_TDF_APP_STAGE_COUNT_BY_DATE: BASE_URL + "/tdf/api/activityStatusLogging",
    ADMIN_TDF_VIEW_JOURNEY: (mob) => BASE_URL + "/tdf/api/getListOfActivity/" + mob,


    // download sample csv
    DOWNLOAD_SAMPLE_FORMAT: (type) => BASE_URL + `/app/tdf/${type}DataFormat`,

    TDF_USER_CREDIT_REPORT_FETCH: (mob) => BASE_URL + "/tdf/api/getReport/" + mob,

    // telecaller dashboard
    TDF_TELECALLER_DASHBOARD_DISPOSITION_LISTING: BASE_URL + "/app/disposition/dispositionList",
    TDF_TELECALLER_DASHBOARD_CARDS: BASE_URL + "/app/tdf/crm/teleCallerCardDetails",
    TDF_TELECALLER_DASHBOARD_AGENT_TASK: (type) => BASE_URL + `/app/tdf/agent${type}Tasks`,
    TDF_TELECALLER_VIEW_SEND_SMS: (mob) => BASE_URL + `/tdf/api/sendSmsCRM/${mob}`,


    TDF_WEBSITE_USER_DETAIL: (mob) => BASE_URL + `/tdf/api/getProfileDetail/${mob}`,


    // raise settlement 
    TELE_CALLER_RAISE_SETTLEMENT: BASE_URL + "/tdf/api/raiseSettlement",
    TELE_CALLER_SETTLEMENT_LIST: (mob) => BASE_URL + "/tdf/api/settlementListing/" + mob,
    POST_CREDIT_INSIGHT_DETAILS: (mob) => BASE_URL + "/tdf/api/userBureauInsight/generalInsight/" + mob,

    TELE_CALLER_UPLOAD_SETTLEMENT_LETTER: BASE_URL + "/tdf/api/uploadSettlementLetter",

    // download csv samples
    PROCESS_ADMIN_DOWNLOAD_SAMPLE_CSV: (type) => BASE_URL + `/app/tdf/${type}DataFormat`,

    // reallocation APIs Start
    // download csv samples for the manager for reallocation
    PROCESS_MANAGER_DOWNLOAD_SAMPLE_CSV: () => BASE_URL + `/app/tdf/processExcel/download/reAllocationLoan`,
    UPLOAD_REALLOCATE_EXCEL_FILE_NEW: () => BASE_URL + `/app/tdf/allocation/reallocateLoan`,
    UPLOAD_REALLOCATE_CARD_EXCEL_FILE_NEW: () => BASE_URL + `/app/tdf/allocation/reallocateCard`,
    UPLOAD_LOAN_ADD_ROW: () => BASE_URL + `/app/tdf/processExcel/loan/addRow`,
    UPLOAD_CARD_ADD_ROW: () => BASE_URL + `/app/tdf/processExcel/card/addRow`,
    PROCESS_MANAGER_DOWNLOAD_SAMPLE_CSV: () => BASE_URL + `/app/tdf/processExcel/download/reAllocationLoan`,
    PROCESS_EXCEL_ADD_COLUMN_DOWNLOAD_LOAN_SAMPLE_CSV: () => BASE_URL + `/app/tdf/processExcel/download/addColumnLoan`,
    PROCESS_MANAGER_DOWNLOAD_CARD_SAMPLE_CSV: () => BASE_URL + `/app/tdf/processExcel/download/reAllocationCard`,
    PROCESS_MANAGER_DOWNLOAD_CARD_SAMPLE_CSV_FOR_ADD_COLUMN_CARD: () => BASE_URL + `/app/tdf/processExcel/download/addColumnCard`,
    UPLOAD_PROCESS_EXCEL_LOAN_ADD_COLUMN: () => BASE_URL + `/app/tdf/processExcel/loan/addColumn`,
    UPLOAD_ADD_COLUMN_CARD_EXCEL_FILE_NEW: () => BASE_URL + `/app/tdf/processExcel/card/addColumn`,
    UPLOAD_PAYMENT_LOAN_EXCEL_FILE_NEW: () => BASE_URL + `/app/tdf/processExcel/payment/v1/genericLoan`,
    UPLOAD_ADD_PAYMENT_CARD_EXCEL_FILE_NEW: () => BASE_URL + `/app/tdf/processExcel/payment/v1/genericCard`,
    UPLOAD_CUMULATIVE_CARD_EXCEL_FILE_NEW: () => BASE_URL + `/app/tdf/processExcel/payment/v2/processCard`,
    UPLOAD_CUMULATIVE_LOAN_EXCEL_FILE_NEW: () => BASE_URL + `/app/tdf/processExcel/payment/v2/processLoan`,
    UPLOAD_AGENTS_PERFORMANCE_FILE_NEW: () => BASE_URL + `/app/tdf/processExcel/agentPerformanceFileUpload`,



    // reallocation APIs end

    // campaign apis
    CAMPAIGN_GENRATE_URL: BASE_URL + "/tdf/api/campaign/generateCampaignUrl",
    CAMPAIGN_GENRATED_URL_LISTING: BASE_URL + "/tdf/api/campaign/campaignListing",
    // Post API for UTM
    UTM_GENRATED_URL_LISTING: BASE_URL + "/tdf/api/utm/listing",

    CAMPAIGN_DELETE_BY_ID: (id) => BASE_URL + "/tdf/api/campaign/deleteCampaign/" + id,


    // BLOG APIS
    CREATE_BLOG_FOR_TDF: BASE_URL + "/tdf/api/blog/addBlog",
    BLOG_LISTING_FOR_TDF: BASE_URL + "/tdf/api/blog/blogListing",
    BLOG_DETAIL_FOR_TDF: (id) => BASE_URL + "/tdf/api/blog/blogDetail/" + id,
    BLOG_EDIT_FOR_TDF: (id) => BASE_URL + "/tdf/api/blog/editBlog/" + id,


    // tdf user allocation process
    TDF_USER_LISTING_BY_MAIL: BASE_URL + "/tdf/api/profile/getProfileListingByAgentEmail",
    TDF_USER_ALLOCATION_TO_TELE_CALLER_BY_TL: BASE_URL + "/tdf/api/profile/teleCallerAllocation",
    TDF_USER_ALLOCATION_TO_TL_BY_ADMIN: BASE_URL + "/tdf/api/profile/teamLeadAllocation",
    TDF_USER_ALLOCATION_TL_TO_TC_FILE_ID: BASE_URL + "/tdf/api/profile/profileDataForTeamLead",

    TDF_USER_ALLOCATION_ADMIN_TO_TL_FILE_ID: BASE_URL + "/tdf/api/profile/profileDataForAdmin",

    TDF_USER_RE_ALLOCATION_TO_TELE_CALLER_BY_TL: BASE_URL + "/tdf/api/profile/reAllocationTeleCaller",


    TDF_USER_RE_ALLOCATION_TO_TEAM_LEAD_BY_ADMIN: BASE_URL + "/tdf/api/profile/reAllocationTeamLead",
    TDF_USER_RESET_TO_TEAM_LEAD_BY_ADMIN: (fileId) => BASE_URL + "/tdf/api/profile/resetTeamLeadAllocation/" + fileId,

    TDF_USER_RESET_TO_TELE_CALLER_BY_TL: (id) => BASE_URL + "/tdf/api/profile/resetTeleCallerAllocation/" + id,
    TDF_USER_DETAIL_LIST_TO_TELE_CALLER_BY_TL: BASE_URL + "/tdf/api/profile/profileDataDetailForTeamLead",

    TDF_USER_DETAIL_LIST_TO_TEAM_LEAD_BY_ADMIN: BASE_URL + "/tdf/api/profile/profileDataDetailForAdmin",

    TDF_USER_BLOCK_DETAIL_TO_TELE_CALLER_BY_TL: (mob) => BASE_URL + "/tdf/api/profile/getProfileDetail/" + mob,


    TDF_PLATFORM_SAVE_DISPOSTION: BASE_URL + "/tdf/api/disposition/saveDisposition",
    TDF_PLATFORM_DISPOSTION_LISTING: (mob) => BASE_URL + "/tdf/api/disposition/getDisposition/" + mob,

    // team lead dahsboard
    TEAM_LEAD_DASHBOARD_LISTING: BASE_URL + "/app/teamLead/agentsSummary",
    TEAM_LEAD_DASHBOARD_STATS: BASE_URL + "/app/teamLead/teamLeadSummary",
    TEAM_LEAD_DASHBOARD_TC_DETAIS: BASE_URL + "/app/teamLead/agentDetails",


    // telecaller platform alloc stats
    PLATFORM_GEN_ALLOC_STATS: (agent) => BASE_URL + "/tdf/api/stats/v2/" + agent,
    PLATFORM_GEN_ALLOC_STATS_FILTERS: (agent) => BASE_URL + "/tdf/api/stats/v2/" + agent + "/filter",
    PLATFORM_ALLOCATION_DISPOSTIONS_STAT: BASE_URL + "/tdf/api/stats/disposition",
    PLATFORM_ALLOCATION_STAT_AGENT_SUMMARY: BASE_URL + "/tdf/api/stats/teamLead/agentsSummary",

    PLATFORM_DISPOSITIONS_DOWNLOAD_BY_TL: BASE_URL + "/tdf/api/download/dispositionByAgent",
    PLATFORM_DISPOSITIONS_TABLE_VIEW: BASE_URL + "/tdf/api/stats/disposition",

    // admin dashboard screen
    PLATFORM_ADMIN_TL_SUMMARY: BASE_URL + "/tdf/api/stats/admin/teamLeadSummary",
    PLATFORM_ADMIN_BADLOAN_LISTING: BASE_URL + "/tdf/api/badLoans/getBadLoans",
    PLATFORM_ADMIN_BADLOAN_DOWNLOAD: BASE_URL + "/tdf/api/download/badLoans",
    PLATFORM_ADMIN_STAGE_STATUS_LOGGING: BASE_URL + "/tdf/api/profile/statusLogging",
    PLATFORM_ADMIN_ACTION_LOGGING: BASE_URL + "/tdf/api/profile/actionLogging",

    // platform data list downloads
    PLATFORM_METADATA_LIST_DOWNLOAD_BY_TL: BASE_URL + "/tdf/api/download/profileTCAllocationMetaData",
    PLATFORM_METADATA_LIST_DETAIL_DOWNLOAD_BY_TL: BASE_URL + "/tdf/api/download/profileTCAllocationMetaDataDetail",
    PLATFORM_METADATA_LIST_DOWNLOAD_BY_ADMIN: BASE_URL + "/tdf/api/download/profileAllocationMetaData",
    PLATFORM_METADATA_LIST_DETAIL_DOWNLOAD_BY_ADMIN: BASE_URL + "/tdf/api/download/profileAllocationMetaDataDetail",


    // campaign apis

    CAMPAIGN_UPLOAD_EXCEL_FILE: BASE_URL + "/tdf/api/campaign/runCampaign",
    CAMPAIGN_APPROVE_REJECT_BY_ADMIN: BASE_URL + "/tdf/api/campaign/approveCampaign",
    CAMPAIGN_RESCHEDULE_BY_ADMIN: BASE_URL + "/tdf/api/campaign/rescheduleCampaign",

    CAMPAIGN_GET_LIST_BY_FILTERS: BASE_URL + "/tdf/api/profile/getProfileCampaignListingByAgentEmail",
    CAMPAIGN_CREATE_CAMPAIGN_BY_PLATFORM_LIST: BASE_URL + "/tdf/api/campaign/runAllocationCampaign",
    CAMPAIGN_DETAIL_BY_ID: (id) => BASE_URL + "/tdf/api/campaign/getCampaign/" + id,
    CAMPAIGN_GET_LIST_BY_FILTERS_CLIENT_ALLOCATION: (type) => BASE_URL + `/app/tdf/${type}/${type}CampaignListing`,
    CAMPAIGN_SUMMARIZED_DETAIL_BY_ID: (id) => `${BASE_URL}/tdf/api/campaign/summarizedCampaignReport?campaignId=${id}`,
    CAMPAIGN_FULL_DETAIL_LIST: `${BASE_URL}/tdf/api/campaign/detailedCampaignReport`,

    // matrix connected attemped
    ADMIN_MATRIX_CHART: BASE_URL + "/tdf/api/metrics/fetchMetrics",
    // list of tc to admin
    ADMIN_LIST_OF_TELE_CALLERS_ADMIN: BASE_URL + "/auth/lms/reportingToAgent",


    // UTM Post API
    UTM_UPLOAD: BASE_URL + "/tdf/api/utm/generateUtmUrl",

    // campiagn template upload


    CAMPAIGN_SMS_ADD_TEMPLATE: BASE_URL + "/tdf/api/dltTemplate/addTemplate",

    CAMPAIGN_EMAIL_ADD_TEMPLATE: BASE_URL + "/tdf/api/email/addTemplate",
    CAMPAIGN_EMAIL_TEMPLATE_DETAIL: (id) => `${BASE_URL}/tdf/api/email/getTemplate/${id}`,
    CAMPAIGN_EMAIL_TEMPLATE_DELETE: (id) => `${BASE_URL}/tdf/api/email/deleteTemplate/${id}`,
    CAMPAIGN_EMAIL_DETAIL_DOWNLOAD: (path) => `${BASE_URL}/tdf/api/download/file?path=${path}`,

    CAMPAIGN_GET_CAMPAIGN_HISTORY: `${BASE_URL}/tdf/api/campaign/getCampaignHistory`,
    // CAMPAIGN_GET_CAMPAIGN_HISTORY: `http://localhost:8000/campaign-history-summary`,
    // 
    CAMPAIGN_GET_CAMPAIGN_PAYMENT_HISTORY: `${BASE_URL}/app/tdf/payment/getPaymentHistory`,
    // 
    CAMPAIGN_RESCHEDULE: `${BASE_URL}/tdf/api/campaign/rescheduleCampaign`,

    // credilio
    CREDILIO_DETAIL: (mob) => `${BASE_URL}/tdf/api/credilio/getDetails/${mob}`,

    // campaign reporting dashb
    CAMPAIGN_REPORTING_DASHBOARD_CHANNEL_STATS: `${BASE_URL}/tdf/api/campaign/getCampaignChannelStats`,


    // dashboard stats
    DASHBOARD_API_TEAMLEAD_SUMMARY: `${BASE_URL}/tdf/api/stats/admin/teamLeadSummary`,
    DASHBOARD_API_ADMIN_SUMMARY: `${BASE_URL}/tdf/api/stats/admin/adminSummary`,


    CAMPAIGN_REPORTING_DASHBOARD_TREADLINE: `${BASE_URL}/tdf/api/campaignStats/getStats`,
    GET_STATE_LIST: `${BASE_URL}/tdf/api/getState`,

    // new client api 
    CLIENT_TEAMLEAD_METADATA_LIST: (type) => `${BASE_URL}/app/tdf/${type}/${type}TeamLeadMetaDataList`,
    CLIENT_TEAMLEAD_METADATA_BY_ID_LIST: (type) => `${BASE_URL}/app/tdf/allocation/${type}TeleCallerAllocation`,
    //Auth API
    ADMIN_TDF_ORG_CREATE: BASE_URL + "/auth/org/create",
    ADMIN_ORG_LISTING: BASE_URL + "/auth/org/listing",
    AUTH_ORG_DETAIL: (id) => BASE_URL + `/auth/org/${id}`,
    AUTH_ORG_DISABLE: (id) => BASE_URL + `/auth/org/disable/${id}`,
    AUTH_ORG_DELETE: (id) => BASE_URL + `/auth/org/delete/${id}`,
    AUTH_ORG_ENABLE: (id) => BASE_URL + `/auth/org/enable/${id}`,
    AUTH_ORG_UPDATE: BASE_URL + "/auth/org/update",
    AUTH_LMS_ORG_USER: BASE_URL + "/auth/lms/orgUsers",
    AUTH_ADD_ORG_USER: BASE_URL + "/auth/lms/addOrgUser",
    AUTH_ORG_REPORTING_TO: BASE_URL + "/auth/lms/reportingToListingByOrg",
    AUTH_ORG_USERS_DETAIL: (id) => BASE_URL + `/auth/lms/getUser/${id}`, //api for org user's details
    AUTH_ADMIN_ORG_DETAIL: (id) => BASE_URL + `/auth/org/admin/details`, //api for org's admin details





    PLATFORM_USER_ACTIONS_LISTING: `${BASE_URL}/tdf/api/userAction/userActionListing`,
    PLATFORM_USER_ACTIONS_DETAIL_LISTING: (mob) => `${BASE_URL}/tdf/api/userAction/userActionListing/${mob}`,
    PLATFORM_USER_ACTION_MARK_AS_RESOLVED: (id) => `${BASE_URL}/tdf/api/userAction/markResolve/${id}`,
    PLATFORM_USER_ACTION_DETAILS: `${BASE_URL}/tdf/api/userAction/userActionListingByMobile`,

    // stitched campaigns
    STITCHED_CAMPAIGN_CREATE_CAMPAIGN: `${BASE_URL}/tdf/api/campaign/stitched`,
    STITCHED_CAMPAIGN_GET_TREE_BY_PARENT: (id) => `${BASE_URL}/tdf/api/campaign/getStitchedCampaignTree/${id}`,
    STITCHED_CAMPAIGN_APPROVE_CAMPAIGN: `${BASE_URL}/tdf/api/campaign/approveStitchedCampaign`,
    STITCHED_CAMPAIGN_RESCHEDULE: `${BASE_URL}/tdf/api/campaign/rescheduleStitchedCampaign`,
    STITCHED_CAMPAIGN_CONTROL_ACTION: `${BASE_URL}/tdf/api/campaign/stitched/control`,


    DIALER_MAP_AGENT_IDS: `${BASE_URL}/auth/lms/bulkDialerAgentUpload`,
    DIALER_PUSH_LEAD: `${BASE_URL}/app/dialer/leadPush`,
    DIALER_BULK_PUSH_LEAD: `${BASE_URL}/app/dialer/bulkLeadPush`,

    CLIENT_ALLOCATION_META_FILE_SUMMARY: `${BASE_URL}/app/api/allocation/dashboard/fileHistoryList`,

    CLIENT_CAMPAIGN_PERFORMANCE_DASHBOARD: `${BASE_URL}/app/campaign/getCampaignStats`,
    CLIENT_DASHBOARD_DISPOSITION_STATS: `${BASE_URL}/app/api/clientDashboard/dispositionStats`,

    CLIENT_ALLOCATION_PAYMENT_META_LIST: (type) => `${BASE_URL}/app/tdf/${type}/${type}PaymentMetaDataList`,
    CLIENT_ALLOCATION_DISPOSITION_STATS: (id) => `${BASE_URL}/app/api/allocation/dashboard/disposition/stats/${id}`,
    CLIENT_ALLOCATION_AGENT_STATS: (id) => `${BASE_URL}/app/api/allocation/dashboard/disposition/agentStats/${id}`,
    CLIENT_ALLOCATION_DISPOSED_STATS: `${BASE_URL}/app/api/allocation/dashboard/pendingStats`,

    CAMPAIGN_DOWNLOAD_DETAIL_REPORT_STATUS: `${BASE_URL}/tdf/api/download/campaignDetailedReport`,
    CAMPAIGN_DOWNLOAD_DETAIL_STITCHED_CAMPAIGN: `${BASE_URL}/tdf/api/download/v2/stitchedCampaignDetailedReport`,

    // stitched campaign template Apis
    STITCHED_CAMPAIGN_TEMPLATE_LIST: `${BASE_URL}/tdf/api/campaign/getAllCampaignTemplates`,
    STITCHED_CAMPAIGN_CREATE_TEMPLATE: `${BASE_URL}/tdf/api/campaign/campaignTemplate/create`,
    STITCHED_CAMPAIGN_EDIT_TEMPLATE: (id) => `${BASE_URL}/tdf/api/campaign/campaignTemplate/edit/${id}`,
    STITCHED_CAMPAIGN_USE_TEMPLATE: (id) => `${BASE_URL}/tdf/api/campaign/campaignTemplate/${id}`,
    CREATE_STITCHED_CAMPAIGN_USING_TEMPLATE: `${BASE_URL}/tdf/api/campaign/createFromTemplate`,

    //  client dashboard apis
    CLIENT_DASH_UNIFIED_ALLOCATION: `${BASE_URL}/app/api/clientDashboard/getUnifiedAllocation`,
    CLIENT_DASH_UNIFIED_PAYMENTS: `${BASE_URL}/app/api/clientDashboard/getUnifiedPayment`,
    CLIENT_DASH_PERFORMANCE_ANALYTICS: `${BASE_URL}/app/api/clientDashboard/performanceAnalytics`,
    CLIENT_DASH_TEAM_HIERARCHY: `${BASE_URL}/app/api/clientDashboard/getTeamHierarchy`,
    CLIENT_DASH_PAYMENTS_AND_COLLECTION: `${BASE_URL}/app/api/clientDashboard/getDashboardDetails`,
    CLIENT_DASHBOARD_AGENT_PERFORMANCE: `${BASE_URL}/app/api/clientDashboard/getAgentPerformance`,
    CAMPAIGN_GET_HOT_LEADS: `${BASE_URL}/app/hotLeads/getLeads`,
    HOT_LEADS_TEAMLEAD_ALLOCATION: `${BASE_URL}/app/hotLeads/teamLeadAllocation`,
    HOT_LEADS_TELECALLER_ALLOCATION: `${BASE_URL}/app/hotLeads/teleCallerAllocation`,
    HOT_LEADS_DOWNLOAD: `${BASE_URL}/app/hotLeads/downloadHotLeads`,
    HOT_LEADS_UNALLOCATED_LEADS: `${BASE_URL}/app/hotLeads/unAllocatedLeads`,
    CAMPAIGN_GET_HOT_LEADS_STATS: `${BASE_URL}/app/hotLeads/hotLeadsStats`,
    CAMPAIGN_GET_MANAGER_ALLOCATION_CONFIG: `${BASE_URL}/app/hotLeads/getManagerAllocationConfig`,
    CAMPAIGN_MANAGER_AUTO_ALLOCATION: `${BASE_URL}/app/hotLeads/saveManagerAllocationConfig`,





    CLIENT_DASHBOARD_AGENT_RECOVERY: `${BASE_URL}/app/api/clientDashboard/recoveryByTeamLead`,

    //  recurring 5jul26 6:15pm
    RECURRING_CAMPAIGN_DETAILS_LIST: (id) => `${BASE_URL}/tdf/api/campaign/getRecurringCampaign/${id}`,
    RECURRING_CAMPAIGN_CREATION: `${BASE_URL}/tdf/api/campaign/recurringCampaign`,

    // 3:06 pm 28 jul 2026 
    TEMPLATE_GET_PROCESSES_LISTING: `${BASE_URL}/auth/org/processListing`,
    TEMPLATE_GET_PROCESSES_LISTING_2: (orgId) => `${BASE_URL}/auth/org/processListing/?orgId=${orgId}`,
    TEMPLATE_GET_DETAILS: (type, id) => `${BASE_URL}/${getUrlForTemplates(type)}/v2/templates/${id}`,
    TEMPLATE_GET_DETAILS_2: (type, id, orgId) => `${BASE_URL}/${getUrlForTemplates(type)}/v2/templates/${id}/?orgId=${orgId}`,
    TEMPLATE_GET_PROCESS_PERMISSION: (type) => `${BASE_URL}/${getUrlForTemplates(type)}/v2/templates/permissions`,
    ADD_CAMPAIGN_TEMPLATE: (type) => `${BASE_URL}/${getUrlForTemplates(type)}/v2/templates`,
    IVR_TEMPLATE_GET_METADATA: `${BASE_URL}/tdf/api/ivrTemplate//v2/templates/all`,
    CAMPAIGN_GET_ALL_SMS_TEMPLATE: BASE_URL + "/tdf/api/dltTemplate/v2/templates/all",
    CAMPAIGN_SMS_LIST: BASE_URL + "/tdf/api/dltTemplate/v2/templates/all",
    CAMPAIGN_EMAIL_LIST: BASE_URL + "/tdf/api/email/v2/templates/all",
    IVR_TEMPLATE_GET_METADATA_FOR_CAMPAIGN: `${BASE_URL}/tdf/api/ivrTemplate/v2/templates/all`,

    GET_TEMPLATE_LIST: (type) => `${BASE_URL}/${getUrlForTemplates(type)}/v2/templates/all`,

    CAMPAIGN_SMS_ADD_BULK_TEMPLATE: BASE_URL + "/tdf/api/dltTemplate/v2/addBulkTemplates",
}

const authorizeRequest = () => {
    const defaultHeaders = {};
    // const AUTH_RESPONSE = GetCookie('USER_AUTH');
    const AUTH_RESPONSE = getLocalStorage('USER_AUTH');
    if (AUTH_RESPONSE) {
        return {
            // 'Authorization': `Bearer ${AUTH_RESPONSE}`
        }
    } else return defaultHeaders;

}

export const API = {

    GET: (url, body = {}, params = {}, cb) => {
        axiosInstance.get(url, {
            headers: authorizeRequest(),
            data: body,
            params: params
        }).then(res => {
            cb(res.data);
        }).catch(err => {
            console.error("ERROR:", err);
            cb(null);
        })
    },
    POST: (url, body = {}, params = {}, cb) => {
        axiosInstance.post(url,
            body, {
            headers: authorizeRequest(),
            params: params
        }).then(res => {
            cb(res.data);
        }).catch(err => {
            console.log("ERROR: ", err);
            cb(err.response?.data || null);
        })
    },
    PATCH: (url, body = {}, params = {}, cb) => {
        axiosInstance.patch(url,
            body, {
            headers: authorizeRequest(),
            params: params
        }).then(res => {
            cb(res.data);
        }).catch(err => {
            console.log("ERROR: ", err);
            cb(null);
        })
    },
    PUT: (url, body = {}, params = {}, cb) => {
        axiosInstance.put(url, body, {
            headers: authorizeRequest(),
            params: params
        })
            .then(res => {
                cb(res.data);
            })
            .catch(err => {
                console.log("ERROR: ", err);
                cb(null);
            });
    },
    DELETE: (url, body = {}, params, cb) => {
        axiosInstance.delete(url, {
            headers: authorizeRequest(),
            params: params,
        }).then(res => {
            cb(res);
        }).catch(err => {
            console.error("ERROR: ", err);
            cb(null);
        })
    },
    // GENERAL: (url, method = "get", body = {}, params = {}, headers = {}, cb) => {
    //     axios({
    //         url: url,
    //         method: method,
    //         data: body,
    //         params: params,
    //         headers: headers,
    //     }).then(res => {
    //         cb(res.data);
    //     }).catch(err => {
    //         console.error("ERROR: ", err);
    //         cb(err);
    //     })
    // },
    GENERAL: (
        url,
        method = "get",
        body = {},
        params = {},
        headers = {},
        cb
    ) => {
        axiosInstance({
            url,
            method,
            data: body,
            params,
            headers: {
                ...headers
            }
        }).then(res => {
            cb(res.data);
        }).catch(err => {
            console.error("ERROR: ", err);
            cb(err);
        })
    },
    DOWNLOAD: (url, body = {}, params = {}, cb) => {
        axiosInstance.get(url, {
            responseType: 'blob',
            headers: authorizeRequest(),
            data: body,
            params: params
        })
            .then((response) => {
                cb(response)
            })
            .catch((err) => {
                console.log(err);
            });
    },
    DOWNLOADWITHHEAD: (url, headers = {}, body = {}, params = {}, cb) => {
        axios({
            url: url,
            method: 'POST',
            responseType: "blob", // important
            headers: headers,
            data: body
        })
            .then((response) => {
                cb(response)
            })
            .catch((err) => {
                console.log(err);
            });
    },
    DOWNLOADWITHHEAD2: (url, headers = {}, body = {}, params = {}, cb) => {
        axiosInstance({
            url: url,
            method: 'POST',
            responseType: "blob", // important
            headers: headers,
            data: body
        })
            .then((response) => {
                cb(response)
            })
            .catch((err) => {
                console.log(err);
            });
    },
    DOWNLOADPOST: (url, body = {}, params = {}, cb) => {
        axios({
            url: url,
            method: 'POST',
            responseType: 'blob', // important
            headers: authorizeRequest(),
            data: body
        }).then((response) => {
            cb(response);
            const url = window.URL.createObjectURL(new Blob([response.data]));
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', 'template.xlsx');
            document.body.appendChild(link);
            link.click()
        }).catch((err) => {
            console.log(err);
        });
    },
    // Download zip file like excel
    DOWNLOADPOSTZIP: (url, body = {}, params = {}, cb, filename = 'template.zip') => {
        axiosInstance({
            url: url,
            method: 'POST',
            responseType: 'blob', // important
            headers: authorizeRequest(),
            data: body
        })
            .then((response) => {
                cb(response);
                const urlObj = window.URL.createObjectURL(new Blob([response.data], { type: 'application/zip' }));
                const link = document.createElement('a');
                link.href = urlObj;
                link.setAttribute('download', filename);
                document.body.appendChild(link);
                link.click();
                document.body.removeChild(link);
            }).catch((err) => {
                console.log(err);
            });
    },

}